import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ErrorMessage } from "./ErrorMessage";
import { useAuth } from "./AuthContext";

export function LoginForm() {
  const { setIsAuthenticated, setAuthToken, setUserData } = useAuth();
  const [emailInput, setEmailInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(false);

  // Check if registration is open
  useEffect(() => {
    axios
      .get("/api/registration-config")
      .then((res) => setRegistrationOpen(res.data.open_registration))
      .catch(() => setRegistrationOpen(false));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setShowError(false);

    try {
      const res = await axios.post("/api/user/login", {
        email_address: emailInput.toLowerCase(),
        password: passwordInput,
      });

      if (res.data.success) {
        const token = res.data.token;
        const userData = res.data.user;

        // Store token in context and localStorage
        setAuthToken(token);
        setUserData(userData);
        
        // Always store token, but handle expiration based on remember me preference
        localStorage.setItem("auth_token", token);
        localStorage.setItem("remember_me", rememberMe.toString());

        setIsAuthenticated(true);
      }
    } catch (error) {
      setShowError(true);
      if (error.response?.data?.detail) {
        setErrorMessage(error.response.data.detail);
      } else {
        setErrorMessage("Login failed. Please check your credentials.");
      }
      setPasswordInput("");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ backgroundColor: "#F0F0F1", minHeight: "100vh", paddingTop: "0px", fontFamily: "'Montserrat', sans-serif" }}>
      <div className="container py-5">
        <div className="d-flex flex-column align-items-center">
          <div className="text-center mb-4">
            <h1 className="h2">Member Login</h1>
          </div>

          {showError && (
            <ErrorMessage 
              title="Login Failed" 
              message={errorMessage} 
            />
          )}

          <div className="card shadow-sm rounded-0 bg-white mt-3" style={{ padding: "30px", width: "350px" }}>
            <form onSubmit={handleLogin}>
              <div className="mb-3">
                <label htmlFor="email" className="form-label">Email Address</label>
                <input
                  type="email"
                  id="email"
                  className="form-control"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="password" className="form-label">Password</label>
                <div className="input-group">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    className="form-control"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  id="rememberMe"
                  className="form-check-input"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <label htmlFor="rememberMe" className="form-check-label">
                  Remember me
                </label>
              </div>

              <button
                type="submit"
                className="btn btn-admin w-100"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Logging in...
                  </>
                ) : (
                  "Log In"
                )}
              </button>
            </form>

            {registrationOpen && (
              <div className="mt-3 text-center">
                <hr />
                <p className="mb-2">Don't have an account?</p>
                <Link to="/register" className="btn btn-custom-outline">
                  Register
                </Link>
              </div>
            )}
 
          </div>
          <Link className="mt-3 small-link" to="/">← Return to Website</Link>
        </div>
      </div>
    </div>
  );
}
